import { IBoardRound, IBoardSnapshot } from '../../../dtos/IBoardSnapshot';

export interface IBoardDiff {
  /** Rodadas encerradas desde a versão anterior, da mais antiga para a mais nova. */
  closedRounds: IBoardRound[];
  /** Slots que passaram a "em andamento". */
  startedSlots: string[];
  /** Posições ganhas (+) ou perdidas (−) por equipe, dentro do grupo. */
  movements: Record<string, number>;
  /** Pontuação anterior e nova por equipe que mudou. */
  scoreChanges: Record<string, { from: number; to: number }>;
  /** Equipes que viraram 1º lugar no grupo. */
  newLeaders: string[];
}

export const EMPTY_DIFF: IBoardDiff = {
  closedRounds: [],
  startedSlots: [],
  movements: {},
  scoreChanges: {},
  newLeaders: [],
};

interface ITeamState {
  group: string;
  position: number | null;
  points: number;
}

function teamStates(snapshot: IBoardSnapshot): Map<string, ITeamState> {
  const map = new Map<string, ITeamState>();
  snapshot.ranking.forEach(group => {
    const key = `${group.tournament_modality_id}:${group.category}`;
    group.entries.forEach(entry => {
      map.set(entry.team_id, {
        group: key,
        position: entry.position,
        points: entry.points,
      });
    });
  });
  return map;
}

/** O que mudou entre duas versões do telão; sem versão anterior, nada anima. */
export function diffSnapshots(
  prev: IBoardSnapshot | null,
  next: IBoardSnapshot,
): IBoardDiff {
  if (!prev) return EMPTY_DIFF;

  const seenRounds = new Set(prev.last_rounds.map(r => r.round_id));
  const closedRounds = next.last_rounds
    .filter(r => !seenRounds.has(r.round_id))
    .sort(
      (a, b) =>
        new Date(a.scored_at).getTime() - new Date(b.scored_at).getTime(),
    );

  const wasOngoing = new Set(
    prev.upcoming.filter(s => s.status === 'ongoing').map(s => s.slot_id),
  );
  const startedSlots = next.upcoming
    .filter(
      s => !s.is_break && s.status === 'ongoing' && !wasOngoing.has(s.slot_id),
    )
    .map(s => s.slot_id);

  const before = teamStates(prev);
  const after = teamStates(next);
  const movements: Record<string, number> = {};
  const scoreChanges: Record<string, { from: number; to: number }> = {};
  const newLeaders: string[] = [];

  after.forEach((now, teamId) => {
    const old = before.get(teamId);
    if (!old || old.group !== now.group) return;

    if (old.points !== now.points) {
      scoreChanges[teamId] = { from: old.points, to: now.points };
    }

    if (
      old.position !== null &&
      now.position !== null &&
      old.position !== now.position
    ) {
      movements[teamId] = old.position - now.position;
    }

    // No início todos empatam em 1º com 0 ponto: o primeiro a pontuar também
    // é "novo líder".
    const wasLeading = old.position === 1 && old.points > 0;
    if (now.position === 1 && now.points > 0 && !wasLeading) {
      newLeaders.push(teamId);
    }
  });

  return { closedRounds, startedSlots, movements, scoreChanges, newLeaders };
}
